// ===========================================
// Newsletters Management Page
// ===========================================
// Admin interface for managing newsletters and blog posts

import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as DuplicateIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useNewsletters,
  useDeleteNewsletter,
  useDuplicateNewsletter,
  usePublishNewsletter,
  useUnpublishNewsletter,
} from '../../hooks/useNewsletter';
import {
  NewsletterListItem,
  NewsletterStatus,
  ContentVisibility,
  NewsletterFilters,
} from '../../types/newsletter.types';

// ===========================================
// STATUS DISPLAY
// ===========================================

const statusColors: Record<NewsletterStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  SCHEDULED: 'info',
  SENDING: 'warning',
  SENT: 'success',
  FAILED: 'error',
};

const visibilityLabels: Record<ContentVisibility, string> = {
  PUBLIC: 'Public Blog',
  PORTAL: 'Portal Only',
  DRAFT: 'Draft',
};

// ===========================================
// COMPONENT
// ===========================================

export default function NewslettersPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  // Filter state
  const [filters, setFilters] = useState<NewsletterFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Context menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNewsletter, setSelectedNewsletter] = useState<NewsletterListItem | null>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newsletterToDelete, setNewsletterToDelete] = useState<NewsletterListItem | null>(null);

  // Queries and mutations
  const { data: newslettersData, isLoading, error } = useNewsletters({
    ...filters,
    search: searchTerm || undefined,
  });
  const deleteMutation = useDeleteNewsletter();
  const duplicateMutation = useDuplicateNewsletter();
  const publishMutation = usePublishNewsletter();
  const unpublishMutation = useUnpublishNewsletter();

  const newsletters = useMemo(() => newslettersData?.data || [], [newslettersData]);

  // Table columns
  const columns: Column<NewsletterListItem>[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
      format: (value, row) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {value as string}
          </Typography>
          {row.excerpt && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 250, display: 'block' }}>
              {row.excerpt}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value as string}
          size="small"
          color={statusColors[value as NewsletterStatus]}
        />
      ),
    },
    {
      id: 'visibility',
      label: 'Visibility',
      minWidth: 120,
      format: (value) => visibilityLabels[value as ContentVisibility] || String(value),
    },
    {
      id: 'author',
      label: 'Author',
      minWidth: 130,
      format: (_, row) => `${row.author.firstName} ${row.author.lastName}`,
    },
    {
      id: 'sentCount',
      label: 'Sent',
      minWidth: 80,
      align: 'right',
      format: (value, row) => {
        const sent = value as number;
        const total = row.totalRecipients;
        return total > 0 ? `${sent}/${total}` : '-';
      },
    },
    {
      id: 'openCount',
      label: 'Opens',
      minWidth: 80,
      align: 'right',
      format: (value, row) => {
        const opens = value as number;
        const sent = row.sentCount;
        if (sent === 0) return '-';
        const rate = ((opens / sent) * 100).toFixed(0);
        return `${opens} (${rate}%)`;
      },
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 100,
      format: (value) => new Date(value as string).toLocaleDateString(),
    },
  ];

  // Handlers
  const handleCreate = () => {
    navigate(`/${schoolSlug}/admin/newsletters/new`);
  };

  const handleEdit = (newsletter: NewsletterListItem) => {
    navigate(`/${schoolSlug}/admin/newsletters/${newsletter.id}`);
  };

  const handleView = (newsletter: NewsletterListItem) => {
    navigate(`/${schoolSlug}/admin/newsletters/${newsletter.id}/preview`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, newsletter: NewsletterListItem) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedNewsletter(newsletter);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedNewsletter(null);
  };

  const handleDuplicate = async () => {
    if (!selectedNewsletter) return;
    try {
      await duplicateMutation.mutateAsync(selectedNewsletter.id);
    } catch (err) {
      console.error('Failed to duplicate newsletter:', err);
    }
    handleMenuClose();
  };

  const handlePublish = async () => {
    if (!selectedNewsletter) return;
    try {
      await publishMutation.mutateAsync(selectedNewsletter.id);
    } catch (err) {
      console.error('Failed to publish newsletter:', err);
    }
    handleMenuClose();
  };

  const handleUnpublish = async () => {
    if (!selectedNewsletter) return;
    try {
      await unpublishMutation.mutateAsync(selectedNewsletter.id);
    } catch (err) {
      console.error('Failed to unpublish newsletter:', err);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (!selectedNewsletter) return;
    setNewsletterToDelete(selectedNewsletter);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!newsletterToDelete) return;
    try {
      await deleteMutation.mutateAsync(newsletterToDelete.id);
      setDeleteDialogOpen(false);
      setNewsletterToDelete(null);
    } catch (err) {
      console.error('Failed to delete newsletter:', err);
    }
  };

  const handleViewAnalytics = () => {
    if (!selectedNewsletter) return;
    navigate(`/${schoolSlug}/admin/newsletters/${selectedNewsletter.id}/analytics`);
    handleMenuClose();
  };

  /**
   * Extra row actions, rendered INSIDE DataTable's own actions cell.
   *
   * WARNING: Do NOT append a second column to `columns`. DataTable renders its
   * own actions column whenever onEdit/onDelete/onView/renderExtraActions is
   * supplied, so appending one produced TWO actions columns and pushed the real
   * controls out of view -- the page looked like it had no actions at all.
   */
  const renderExtraActions = (row: NewsletterListItem) => (
    <Tooltip title="More actions">
      <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
        <MoreIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      <PageHeader
        title="Newsletters & Blog"
        subtitle="Create and manage newsletters and blog posts"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create Newsletter
          </Button>
        }
      />

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={(e) => setFilters({ ...filters, status: e.target.value as NewsletterStatus || undefined })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            <MenuItem value="SENT">Sent</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={filters.visibility || ''}
            label="Visibility"
            onChange={(e) => setFilters({ ...filters, visibility: e.target.value as ContentVisibility || undefined })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PUBLIC">Public Blog</MenuItem>
            <MenuItem value="PORTAL">Portal Only</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load newsletters: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      )}

      <DataTable<NewsletterListItem>
        columns={columns}
        renderExtraActions={renderExtraActions}
        data={newsletters}
        loading={isLoading}
        onEdit={handleEdit}
        emptyMessage="No newsletters found. Create your first newsletter to get started."
      />

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleView(selectedNewsletter!); handleMenuClose(); }}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} /> Preview
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <DuplicateIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        {selectedNewsletter?.status === 'DRAFT' && (
          <MenuItem onClick={handlePublish}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} /> Publish
          </MenuItem>
        )}
        {(selectedNewsletter?.status === 'SENT' || selectedNewsletter?.visibility === 'PUBLIC') && (
          <MenuItem onClick={handleUnpublish}>
            <ScheduleIcon fontSize="small" sx={{ mr: 1 }} /> Unpublish
          </MenuItem>
        )}
        {selectedNewsletter?.status === 'SENT' && (
          <MenuItem onClick={handleViewAnalytics}>
            <AnalyticsIcon fontSize="small" sx={{ mr: 1 }} /> Analytics
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Newsletter"
        message={`Are you sure you want to delete "${newsletterToDelete?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setNewsletterToDelete(null);
        }}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
